<!-- HelpModal.vue -->
<template>
  <div class="modal-container">
    <img
      src="images/close.svg"
      class="close"
      @click="resetModal"
      role="button"
      aria-label="Close"
      alt="X to close modal"
      title="Close"
    />
    <div class="modal-content">
      <transition-group :name="titleFade">
        <h2 :key="title">{{ title }}</h2>
      </transition-group>
      <div class="modal-main" ref="ancestor">
        <knot-carousel
          :selected-slide="selectedSlide"
          @page-changed="pageChange"
        >
          <template v-slot:goal>
            <h2>Goal</h2>
            <p>
              Your goal is to make <strong>three sequences</strong> using <strong>{{numberUsage}}</strong> daily numbers.
            </p>
            <ul>
              <li>
                Each sequence is made of <strong>four numbers</strong> that are in
                increasing order.
              </li>

              <li>
                Starting from the third number, every number is the sum of the
                two before it. Just like the
                <a href="https://en.wikipedia.org/wiki/Fibonacci_sequence"
                  >Fibonacci sequence</a
                >!
              </li>

              <li>
                One number, the "<strong class="knot-color">knot</strong
                >", appears in all three sequences.
              </li>

              <li>
                The other <strong>nine numbers</strong> are each used only once.
              </li>
            </ul>
            <p>
              Only one set of three sequences satisfies all of these rules, though
              <strong>4 to 10 individual sequences</strong> appear each day.
            </p>
            <img src="images/fibonacci-like-example-detailed.svg" class="fibonacci-like-example">
            <p class="img-caption">
              <strong>A Fibonacci-like sequence</strong>
            <table id="fibonacci-like-info">
              <tbody>
                <tr>
                  <td>2</td>
                  <td>+</td>
                  <td>5</td>
                  <td>=</td>
                  <td>7</td>
                </tr>
                 <tr>
                  <td>5</td>
                  <td>+</td>
                  <td>7</td>
                  <td>=</td>
                  <td>12</td>
                </tr>
              </tbody>
            </table>
            </p>
          </template>
          <template v-slot:instructions>
            <h2>Instructions</h2>
            <p>
              <strong>Enter Sequences:</strong> Enter
              <strong>four</strong> numbers. Sequences are automatically entered
              after the fourth number is selected.
            </p>
            <p>
              <strong>Choose Sequences for Submission:</strong> Tap sequences to
              select them. Your answer is submitted automatically once
              <strong>three</strong> sequences are selected.
            </p>
            <p>
              The timer will stop once you have selected the three solution
              sequences. Any valid sequences you did not find will then be
              revealed.
            </p>
            <h2 style="padding-top:3.5vh">How to Play with SamWords</h2>
            <a class="youtube-link" href="https://youtu.be/HjVVFnH95hE?si=ZBGspCbJvka77NTZ" target="_blank">
              <img src="images/samwords.jpg" alt="How to Play Video Thumbnail" title="How to Play Video Link">
            </a>
          </template>
          <template v-slot:sequence-test>
            <h2 class="centered-text">Practice Finding the Knot</h2>
            <div class="centered-text">This sample board shows all sequences for a <strong>Level {{level}}</strong> puzzle. Tap any three to see why they do or do not isolate the <strong class="knot-color">knot</strong>. A quick, hands-on way to learn the logic before diving in.</div>
            <knot-board-demo
            :level="level"></knot-board-demo>
          </template>
          <template v-slot:additional>
            <h2>Need a Hint?</h2>
            <p>
              Feeling stuck? Tap a lock icon
              <img
                src="images/lock.svg"
                class="inline-img"
                alt="Line art lock"
                title="Lock icon for unused hints"
              />
              in the top left to reveal one of the solution sequences.
            </p>
            <h2>Need More of a Challenge?</h2>
            <p>
              Before starting a puzzle take a moment to choose your preferred
              level and mode.
            </p>
            <ul>
              <li>
                <strong>Level:</strong> Sets how many numbers are in the puzzle
              </li>
              <li>
                <strong>Mode:</strong>
                <ul>
                  <li>
                    <strong>EASY:</strong> Includes visual cues to guide you as
                    you solve
                  </li>
                  <li>
                    <strong>HARD:</strong> Removes these aids, offering an extra
                    layer of challenge
                  </li>
                </ul>
              </li>
            </ul>
          </template>
          <template v-slot:privacy>
            <p>
              We value your privacy and aim to be clear about how we handle your
              data.
            </p>
            <p>
              <strong>How We Use Data:</strong> When a game is completed,
              anonymous metrics are sent to our server to help us build
              community benchmarks and improve the game. No personal information
              is ever included.
            </p>
            <p>
              <strong>Website Analytics:</strong> We use
              <a href="https://umami.is/">Umami Analytics</a>, a privacy-focused
              tool that collects anonymous usage data (like page views and
              device type) without cookies or personal tracking.
            </p>
            <p>
              <strong>What We Store Locally:</strong> If you choose to save
              progress, your game stats and puzzle history are stored on your
              device, and a random UUID is assigned on the server. This UUID
              links your games for metrics while keeping your identity
              anonymous.
            </p>
            <p>
              <strong>Your Consent:</strong> By playing, you agree to the
              collection of anonymous gameplay data (linked with a UUID if
              progress saving is enabled) and anonymous website usage stats.
              Enabling progress saving also consents to the use of local
              storage.
            </p>
            <p>
              <strong>Data Management:</strong> You can clear local storage
              anytime through the in-game toggle or your browser settings. This
              deletes your saved stats and puzzle history from your device. Data
              already sent to the server cannot be deleted, as it is anonymous
              and not tied to you.
            </p>
            <p class="centered-text">Thank you for playing!</p>
            <div class="modal-options">
              <div class="modal-option-label">Save Progress</div>
              <label class="switch">
                <input type="checkbox" v-model="toggleValue" />
                <span class="slider"></span>
              </label>
            </div>
          </template>
        </knot-carousel>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HelpModal",
  props: {
    resetModal: {
      type: Function,
      required: true,
    },
    saveProgress: {
      type: Boolean,
      required: true,
    },
    level: {
      type: Number,
      required: true,
    },
    selectedSlide: {
      type: String,
    },
    defaultTitle: {
      type: String,
      default: "How To Play",
    },
    titlesObject: {
      type: Object,
      default: () => ({
        privacy: "Privacy and Data Usage Notice",
        social: "Social Media",
      }),
    },
  },
  data() {
    return {
      fibonacci: [0, 1, 1, 2, 3, 5, 8],
      toggleValue: this.saveProgress,
      title:
        this.selectedSlide in this.titlesObject
          ? this.titlesObject[this.selectedSlide]
          : this.defaultTitle,
      titleFade: null,
    };
  },
  watch: {
    toggleValue(value) {
      this.$emit("toggled", "saveProgress", value);
    },
  },
  computed: {
    numberUsage() {
      if (this.level === 10) {
        return "all 10";
      } else {
        return `10 of the ${this.level}`;
      }
    },

    fibonacciHeaders() {
      let result = [];
      this.fibonacci.forEach((value, index) => {
        result.push(value);
        if (index < this.fibonacci.length - 1) {
          result.push("");
        }
      });
      return result;
    },
  },
  methods: {
    pageChange(newPageIndex, oldPageIndex, behavior, newPageName) {
      this.title = this.titlesObject[newPageName] || this.defaultTitle;
      if (behavior === "smooth") {
        this.titleFade =
          oldPageIndex < newPageIndex ? "move-right-fade" : "move-left-fade";
      } else {
        this.titleFade = "none";
      }
    },
  },
};
</script>

<style scoped>
.example-img {
  max-width: 20vh;
  align-self: center;
}
ul {
  margin: 0;
  display: flex;
  flex-direction: column;
  row-gap: 1vh;
  padding: 5px 15px 5px 25px;
}

#fibonacci-info {
  margin: 0 auto;
  text-align: center;
  table-layout: fixed;
  height: 3em;
  overflow: scroll;
}

#fibonacci-info tbody {
  height: 5em;
  overflow-y: scroll;
}
td {
  padding: 0 0.5vmin;
  margin: 0;
}
#fibonacci-like-info {
  margin: 0 auto;
  text-align: center;
  table-layout: fixed;
}
#fibonacci-info th {
  font-size: 1.125em;
}
#fibonacci-info .highlight {
  color: var(--border-color);
  font-weight: bold;
}
.img-caption {
  text-align: center;
  margin: 0 10vmin;
}
.socials {
  margin: 10px auto;
  column-gap: 2vh;
}
.socials img {
  width: 6vh;
  margin: 0.5vh;
  transition: all 0.125s ease;
}

.socials img:hover {
  width: 7vh;
  margin: 0;
}
.hashtag {
  color: var(--border-color);
  font-weight: bold;
}
.tooltip {
  position: relative;
  cursor: help;
  display: inline-block;
  border-bottom: 1px dotted var(--primary-color);
}

.tooltip-text {
  visibility: hidden;
  background-color: var(--background-color);
  color: #fff;
  text-align: left;
  padding: 1vh;
  border: 1px solid var(--board-cell-color);
  border-radius: 1vh;
  position: absolute;
  z-index: 100;
  bottom: 50%;
  left: 50%;
  transform: translate(-30%, 50%);
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tooltip:hover .tooltip-text,
.tooltip:focus .tooltip-text,
.tooltip.active .tooltip-text {
  visibility: visible;
  opacity: 0.95;
}
.fibonacci-like-example {
  height: 120px;
  max-width: 100%;
  margin: 0 auto;
}
.youtube-link {
  border: 0;
}
.youtube-link img {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 1.5vh;
}
</style>
